import React, { useRef, useEffect, useCallback } from 'react';
import { Box, Button, Typography } from '@mui/material';

// A box to draw a signature in, by finger, pen or mouse. Reports a PNG data URL
// on every stroke, or null once cleared. Pointer events cover all three inputs;
// touch-action:none stops the page scrolling while someone signs on a phone.
export default function SignaturePad({ onChange, height = 160, disabled = false }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const inked = useRef(false);
  // Held in a ref so a parent passing a fresh function each render doesn't
  // re-run fit() and wipe the signature.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Size the drawing buffer to the box as laid out, at the screen's pixel
  // density, so strokes are crisp and land under the finger.
  const fit = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const { width } = canvas.getBoundingClientRect();
    canvas.width = Math.round(width * ratio);
    canvas.height = Math.round(height * ratio);
    const ctx = canvas.getContext('2d');
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#111';
    inked.current = false;
    onChangeRef.current?.(null);
  }, [height]);

  useEffect(() => { fit(); }, [fit]);

  const point = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const down = (e) => {
    if (disabled) return;
    e.preventDefault();
    canvasRef.current.setPointerCapture?.(e.pointerId);
    drawing.current = true;
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = point(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + 0.1, y + 0.1);
    ctx.stroke();
  };

  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = point(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    inked.current = true;
  };

  const up = () => {
    if (!drawing.current) return;
    drawing.current = false;
    if (inked.current) onChangeRef.current?.(canvasRef.current.toDataURL('image/png'));
  };

  return (
    <Box>
      <Box sx={{ position: 'relative', border: '1px solid #bbb', borderRadius: 1, backgroundColor: '#fff' }}>
        <canvas
          ref={canvasRef}
          style={{ width: '100%', height, display: 'block', touchAction: 'none', cursor: disabled ? 'default' : 'crosshair' }}
          onPointerDown={down}
          onPointerMove={move}
          onPointerUp={up}
          onPointerLeave={up}
          onPointerCancel={up}
        />
        <Box sx={{ position: 'absolute', left: 16, right: 16, bottom: 36, borderBottom: '1px solid #ddd', pointerEvents: 'none' }} />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
        <Typography variant="caption" sx={{ color: '#888' }}>Sign above with your finger, pen or mouse</Typography>
        <Button size="small" onClick={fit} disabled={disabled}>Clear</Button>
      </Box>
    </Box>
  );
}
